import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  defer,
  EMPTY,
  finalize,
  map,
  Observable,
  retry,
  Subscription,
  switchMap,
  throwError,
  timer,
} from 'rxjs';

import * as bcrypt from 'bcryptjs';

import { IHDresponse } from 'src/app/shared/interfaces/IHDdata';
import { IProcessOrder } from 'src/app/shared/interfaces/IProcessOrder';
import { setSessionActive } from 'src/app/stores/global';
import { LOADS_HARD_RELOAD_THRESHOLD, MAX_RETRY_CODE_GENERATION } from 'src/environments/runtime';

import { OverflowAreaDialogComponent } from '../components/overflow-area-dialog/overflow-area-dialog.component';
import { IScheduleActionParams } from '../interfaces/IScheduleActionParams';
import { AccessCode, DEVICE_TYPES, DeviceType } from '../interfaces/ISettings';
import { CreateSchedule } from '../models/schedule.models';
import { CreateUser } from '../models/user.models';
import { ApiErrorHandlerService } from '../services/api-error-handler/api-error-handler.service';
import { FrodoSettings } from '../services/appconfig/settings.model';
import { HelmsdeepService } from '../services/helmsdeep/helmsdeep.service';
import { IdleService } from '../services/idle/idle.service';
import { KillSwitchService } from '../services/kill-switch';
import { LoadFlowOriginService } from '../services/load-flow-origin/load-flow-origin.service';
import { SettingsService } from '../services/settings/settings.service';

import {
  ACTION_TYPES,
  ALREADY_EXISTS_MSG,
  CODE_NOT_FOUND,
  DELIVERY_LIST_TYPE,
  DEVICE_VIEW_CONTEXT_MAP,
  DISPENSE_ACTIONS,
  MENU_URLS,
  MODAL_OPTIONS_OVERFLOW,
  ORDER_ID_IN_USE_MSG,
  SCAN_TYPE,
  SETUP_PATHS,
  TOAST_TYPES,
  VIEW_TYPES,
} from './constants';

interface Params {
  type?: string;
  flow?: string;
  title?: string;
  scheduleId?: string;
}

@Injectable({ providedIn: 'root' })
export class Utils {
  private selectSpacesToLoad = 'common.selectSpacesToLoad';
  private invalidCodeMessage = 'common.invalidCodeMsg';

  deviceType!: DeviceType;
  isLoading: BehaviorSubject<boolean>;
  subscription: Subscription = new Subscription();
  state: RouterStateSnapshot;
  allowAlphanumeric: boolean;
  accessCodeSettings!: AccessCode;
  expireLoaded: boolean;
  guidedLoadFailForward: boolean;
  frodoSettings!: FrodoSettings;

  constructor(
    private apiErrorHandlerService: ApiErrorHandlerService,
    private router: Router,
    private helmsdeepService: HelmsdeepService,
    private settingsService: SettingsService,
    private idleService: IdleService,
    private dialog: MatDialog,
    @Inject('Window') private window: Window,
    private killSwitchService: KillSwitchService,
    private store: Store,
    private loadFlowOrigin: LoadFlowOriginService
  ) {
    this.state = router.routerState.snapshot;
    this.isLoading = new BehaviorSubject<boolean>(false);
    this.settingsService.getSettings.subscribe((settings) => {
      this.deviceType = settings?.device?.deviceType || DEVICE_TYPES.ORDER_HQ_FRONT;
      this.frodoSettings = settings.apps?.frodo || ({} as FrodoSettings);
      this.accessCodeSettings = settings.accessCode || ({} as AccessCode);
    });
  }

  public redirect(url: string, params?: object): Promise<boolean> {
    return params ? this.router.navigate([url, params]) : this.router.navigate([url]);
  }

  public thresholdRedirect(url: string, params?: object, refresh = false) {
    if (Number(LOADS_HARD_RELOAD_THRESHOLD) <= 0 || isNaN(Number(LOADS_HARD_RELOAD_THRESHOLD))) {
      return this.redirect(url, params);
    }
    if (typeof this.window.loadOrderCounter !== 'number') {
      this.window.loadOrderCounter = 0;
    }
    this.window.loadOrderCounter++;

    if (this.window.loadOrderCounter >= +LOADS_HARD_RELOAD_THRESHOLD) {
      if (refresh) {
        this.redirect(url, params).then(() => {
          this.window.location.reload();
        });
      }

      const route = params ? [url, params] : [url];

      this.window.location.href = this.router.serializeUrl(this.router.createUrlTree(route));
      this.window.loadOrderCounter = 0;
    } else {
      this.redirect(url, params);
    }
  }

  public handleRedirection(
    url: string,
    params?: Params,
    orderOverflowArea = false,
    isFrontLoading = false
  ): void {
    // avoid redirect when the app is FLF and the order is overflow area
    if (orderOverflowArea && isFrontLoading) return;
    const navigationPath = params ? [url, params] : [url];
    this.router.navigate(navigationPath);
  }

  public createOrderRequest(
    preparerId: string,
    code: string,
    flow: string,
    expireLoaded: boolean,
    deliveryProvider: string
  ): Observable<any> {
    return defer((): Observable<any> => {
      const order = new CreateSchedule(
        preparerId,
        this.accessCodeSettings,
        '',
        '',
        code,
        flow,
        expireLoaded,
        deliveryProvider
      ).newSchedule;
      console.log('%c request!', 'background: #222; color: #bada55');
      return this.helmsdeepService.createOrder(order);
    });
  }

  public retryStrategy(initialCode: string, delayDurationMs = 1000) {
    const maxRetries = parseInt(MAX_RETRY_CODE_GENERATION, 10) - 1;
    return (src: Observable<any>) =>
      src.pipe(
        retry({
          count: maxRetries,
          delay: (error, retryAttempt) => {
            const errorTitle = error?.error?.title || '';
            console.log('%c retryAttempt: ' + retryAttempt, 'background: #fff; color: red');
            if (
              errorTitle.includes(ALREADY_EXISTS_MSG) ||
              errorTitle.includes(ORDER_ID_IN_USE_MSG) ||
              errorTitle.includes(initialCode)
            ) {
              return timer(delayDurationMs);
            }

            return throwError(() => error);
          },
        }),
        finalize(() =>
          console.log('%c retry process has done!', 'background: #222; color: #bada55')
        )
      );
  }

  private redirectToScreenType(processOrder: Partial<IProcessOrder>): void {
    const { frodoContext, targetScreen, type, flow, enableOverflowArea } = processOrder;
    const isFrontLoading = frodoContext === VIEW_TYPES.FRONT_LOADING;
    const defaultUrl = `${frodoContext}/${targetScreen}`;
    const url = this.loadFlowOrigin.consumeExit(defaultUrl);
    const isMenuOption = MENU_URLS.includes(targetScreen);
    // The origin screen entered the flow on its own, so it takes no order params back.
    const params = url !== defaultUrl || isMenuOption ? null : { type, flow };
    this.setLoadingValue(false);
    this.handleRedirection(url, params, enableOverflowArea, isFrontLoading);
  }

  private deleteOrder(processOrder: Partial<IProcessOrder>): void {
    this.subscription.add(
      this.helmsdeepService.deleteOrder(processOrder.scheduleId).subscribe(
        () => this.redirectToScreenType(processOrder),
        (error) => {
          this.apiErrorHandlerService.getErrorMessage(error);
          this.redirectToScreenType(processOrder);
        }
      )
    );
  }

  cancelLoadInOverflowArea(processOrder: Partial<IProcessOrder>): void {
    if ([SCAN_TYPE, DELIVERY_LIST_TYPE].includes(processOrder.type)) {
      this.setLoadingValue(false);
      return;
    }
    this.cancelLoading(processOrder);
  }

  public cancelLoading(processOrder: Partial<IProcessOrder>): void {
    this.setLoadingValue(true);
    const { frodoContext, scheduleId, type } = processOrder;
    const isFrontLoading = frodoContext === VIEW_TYPES.FRONT_LOADING;
    const isRetail = frodoContext === VIEW_TYPES.RETAIL;
    const side = isFrontLoading || isRetail ? 'FRONT' : null;
    const integratedTypes = [SCAN_TYPE, DELIVERY_LIST_TYPE];
    const isCoreType = !integratedTypes.includes(type);
    this.subscription.add(
      this.helmsdeepService.unloadOrder(scheduleId, side, type).subscribe(
        () => {
          isCoreType ? this.deleteOrder(processOrder) : this.redirectToScreenType(processOrder);
        },
        (_error) => {
          isCoreType ? this.deleteOrder(processOrder) : this.redirectToScreenType(processOrder);
        }
      )
    );
  }

  completeLoadInOverflowAreaByContext(processOrder: Partial<IProcessOrder>): void {
    if (processOrder.frodoContext === VIEW_TYPES.FRONT_LOADING) {
      processOrder.targetScreen = 'create-order';
      this.loadOrder(processOrder);
    } else {
      this.setLoadingValue(false);
      this.redirect(`${processOrder.frodoContext}/processing-order`, {
        type: processOrder.type,
        flow: processOrder.flow,
        enableOverflowArea: processOrder.enableOverflowArea,
        scheduleId: processOrder.scheduleId,
      });
    }
  }

  loadProcessByContext(processOrder: Partial<IProcessOrder>): void {
    if (this.deviceType === DEVICE_TYPES.ORDER_HQ_FRONT) {
      this.setLoadingValue(false);
      this.redirect(`${processOrder.frodoContext}/${processOrder.targetScreen}`, {
        title: this.selectSpacesToLoad,
        type: processOrder.type,
        flow: processOrder.flow,
        scheduleId: processOrder.scheduleId,
      });
    } else {
      this.loadOrder(processOrder);
    }
  }

  handleLoadProcess(processOrder: Partial<IProcessOrder>): void {
    if (processOrder.enableOverflowArea) {
      this.loadOverflowOrder(processOrder);
    } else {
      this.loadProcessByContext(processOrder);
    }
  }

  loadOverflowOrder(processOrder: Partial<IProcessOrder>): void {
    if (processOrder.frodoContext === VIEW_TYPES.FRONT_LOADING) this.idleService.stopTimer();
    const dialogInstance = this.dialog.open(OverflowAreaDialogComponent, {
      disableClose: true,
      backdropClass: 'modal-blurred-background',
    });
    dialogInstance.afterClosed().subscribe((option: string) => {
      if (
        processOrder.frodoContext === VIEW_TYPES.FRONT_LOADING &&
        this.deviceType === DEVICE_TYPES.ORDER_HQ_FRONT
      )
        this.idleService.startTimer();
      if (option === MODAL_OPTIONS_OVERFLOW.cancel) this.cancelLoadInOverflowArea(processOrder);
      if (option === MODAL_OPTIONS_OVERFLOW.complete)
        this.completeLoadInOverflowAreaByContext(processOrder);
    });
  }

  buildAndExecuteScheduleAction(processOrder: IProcessOrder) {
    if (!processOrder.scheduleId) return;

    const actionTypeMapping = {
      [ACTION_TYPES.DELIVERY]: DISPENSE_ACTIONS.DO_LOAD,
      [ACTION_TYPES.PICKUP]: DISPENSE_ACTIONS.DO_DISPENSE,
      [ACTION_TYPES.RECLAIM]: DISPENSE_ACTIONS.DO_RECLAIM,
    };

    const scheduleActionParams: IScheduleActionParams = {
      scheduleId: processOrder.scheduleId,
      type: DISPENSE_ACTIONS.DO_TRANSACTION,
      actionType: actionTypeMapping[processOrder.action],
      smartDoor: !!processOrder.smartDoor,
      side: processOrder.side,
      dispenses: processOrder.dispenseId ? [processOrder.dispenseId] : undefined,
      hmi: false,
      spaceId: processOrder.spaceId,
      ...(processOrder.size ? { size: processOrder.size } : {}),
    };

    return this.helmsdeepService.scheduleAction(scheduleActionParams);
  }

  processOrderByActionType(processOrder: IProcessOrder): Observable<{
    position: string;
    spaceId: string;
    direction: string;
  }> {
    return this.buildAndExecuteScheduleAction(processOrder).pipe(
      switchMap((response: IHDresponse) => {
        const isArray = Array.isArray(response);

        if (!isArray && response.title === CODE_NOT_FOUND) {
          this.setLoadingValue(false);
          this.apiErrorHandlerService.showMessage(this.invalidCodeMessage, TOAST_TYPES.Error);
          return EMPTY;
        }

        if (!isArray) return EMPTY;

        const dispenseId = response[0][0].source;

        return this.helmsdeepService.getDispenseById(dispenseId).pipe(
          map((dispensesNspaces: any) => {
            return {
              position: dispensesNspaces.spaceInfo.hardwareInfo.position,
              spaceId: dispensesNspaces.spaceInfo.id,
              direction: dispensesNspaces.spaceInfo.hardwareInfo.direction,
            };
          }),
          finalize(() => this.setLoadingValue(false))
        );
      }),
      catchError((error) => {
        this.setLoadingValue(false);
        this.apiErrorHandlerService.getErrorMessage(error);
        return EMPTY;
      })
    );
  }

  getLoadingValue(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  setLoadingValue(newValue): void {
    this.isLoading.next(newValue);
  }

  /**
   *   Function creates and returns the order structure
   * @param preparerId
   * @param userId
   * @param flow
   * @param expireLoaded
   * @returns
   */
  createOrderStruct(
    preparerId: string,
    userId: string,
    flow: string,
    expireLoaded: boolean,
    deliveryProvider: string
  ) {
    return new CreateSchedule(
      preparerId,
      this.accessCodeSettings,
      userId,
      '',
      '',
      flow,
      expireLoaded,
      deliveryProvider
    ).newSchedule;
  }

  loadOrder(processOrder: Partial<IProcessOrder>): void {
    const { scheduleId, side, enableOverflowArea, frodoContext, targetScreen, type, flow } =
      processOrder;
    const isFrontLoading = frodoContext === VIEW_TYPES.FRONT_LOADING;
    const url = `${frodoContext}/${targetScreen}`;
    const params = { type, flow, scheduleId };
    this.subscription.add(
      this.helmsdeepService.loadOrder(scheduleId, side, enableOverflowArea).subscribe(
        () => {
          this.setLoadingValue(false);
          this.handleRedirection(url, params, enableOverflowArea, isFrontLoading);
        },
        (error) => {
          this.setLoadingValue(false);
          this.apiErrorHandlerService.getErrorMessage(error);
        }
      )
    );
  }

  validatePassword(currentValue, url, errorMsg): void {
    let isAuthorized = false;
    const isConfigPath = url.includes(SETUP_PATHS.CONFIGURATION);
    if (isConfigPath) {
      const password = this.frodoSettings.setup.password;
      isAuthorized = !!password && bcrypt.compareSync(currentValue, password);
    } else {
      const password = this.frodoSettings.flf.password;
      isAuthorized = currentValue === password;
    }

    if (isAuthorized) {
      if (!isConfigPath) {
        this.store.dispatch(setSessionActive({ value: true }));
        this.killSwitchService.handleKillSwitchMode();
      }
      this.router.navigate([url]);
    } else {
      this.apiErrorHandlerService.handleApiError(null, errorMsg);
      if (isConfigPath) {
        const contextUrl = DEVICE_VIEW_CONTEXT_MAP[this.deviceType];
        this.router.navigate([contextUrl]);
      }
    }
  }

  createOrderByOrderIdType(processOrder: IProcessOrder): Observable<any> {
    return this.helmsdeepService
      .getPreparers()
      .pipe(
        switchMap((preparers: any) =>
          this.createOrderRequest(
            preparers[0].id,
            processOrder.code,
            processOrder.flow,
            processOrder.expireLoaded,
            processOrder.deliveryProvider
          ).pipe(this.retryStrategy(processOrder.code))
        )
      );
  }

  createOrderByMobileType(processOrder: IProcessOrder): Observable<any> {
    return this.helmsdeepService.getPreparers().pipe(
      switchMap((preparers: any) =>
        this.helmsdeepService.getEndUserBySelector({ mobile: processOrder.code }).pipe(
          switchMap((endUserFound: any) => {
            if (endUserFound.length > 0) {
              const userId = endUserFound[0].id;
              const order = this.createOrderStruct(
                preparers[0].id,
                userId,
                processOrder.flow,
                processOrder.expireLoaded,
                processOrder.deliveryProvider
              );
              return this.helmsdeepService.createOrder(order);
            } else {
              const userPayload = new CreateUser(processOrder.code);
              return this.helmsdeepService.createUser(userPayload).pipe(
                switchMap((userResponse: any) => {
                  const userId = userResponse.id;
                  const order = this.createOrderStruct(
                    preparers[0].id,
                    userId,
                    processOrder.flow,
                    processOrder.expireLoaded,
                    processOrder.deliveryProvider
                  );
                  return this.helmsdeepService.createOrder(order);
                })
              );
            }
          })
        )
      )
    );
  }
}
